import { defineComponent as _defineComponent } from 'vue'
import { toDisplayString as _toDisplayString, createElementVNode as _createElementVNode, unref as _unref, createVNode as _createVNode, openBlock as _openBlock, createElementBlock as _createElementBlock, createCommentVNode as _createCommentVNode, createTextVNode as _createTextVNode, normalizeClass as _normalizeClass, createBlock as _createBlock } from "vue"

const _hoisted_1 = { class: "app-budget-content" }
const _hoisted_2 = { class: "app-header" }
const _hoisted_3 = { class: "app-title" }
const _hoisted_4 = { class: "app-actions" }
const _hoisted_5 = {
  key: 0,
  class: "app-loading"
}
const _hoisted_6 = {
  key: 1,
  class: "app-error"
}
const _hoisted_7 = {
  key: 2,
  class: "app-report"
}
const _hoisted_8 = { class: "app-section-title" }
const _hoisted_9 = { class: "app-summary-grid" }
const _hoisted_10 = { class: "app-summary-item" }
const _hoisted_11 = { class: "app-summary-item__label" }
const _hoisted_12 = { class: "app-summary-item__value app-summary-item__value--budget" }
const _hoisted_13 = { class: "app-summary-item" }
const _hoisted_14 = { class: "app-summary-item__label" }
const _hoisted_15 = { class: "app-summary-item__value app-summary-item__value--consumed" }
const _hoisted_16 = { class: "app-summary-item" }
const _hoisted_17 = { class: "app-summary-item__label" }
const _hoisted_18 = { class: "app-summary-item__value app-summary-item__value--paid" }
const _hoisted_19 = { class: "app-summary-item" }
const _hoisted_20 = { class: "app-summary-item__label" }
const _hoisted_21 = { class: "app-summary-item__value app-summary-item__value--negative" }
const _hoisted_22 = { class: "app-progress-item" }
const _hoisted_23 = { class: "app-progress-header" }
const _hoisted_24 = { class: "app-progress-title" }
const _hoisted_25 = { class: "app-progress-percentage" }
const _hoisted_26 = { class: "app-progress-item" }
const _hoisted_27 = { class: "app-progress-header" }
const _hoisted_28 = { class: "app-progress-title" }
const _hoisted_29 = { class: "app-progress-percentage" }
const _hoisted_30 = { class: "app-details" }
const _hoisted_31 = { class: "app-tabs" }

import reportService from '@/services/report.service.index';
import { formatPercentage } from '@/utils/formatters';
import { formatCurrencyWithUserPreference } from '@/utils/currencyUtils';
import TaskBudgetReport from './TaskBudgetReport.vue';
import IntervenantBudgetReport from './IntervenantBudgetReport.vue';
import Button from 'primevue/button';
import { useI18n } from 'vue-i18n';
import { useNotifications } from '@/composables/useNotifications';
import { ProgressSpinner, ProgressBar } from 'primevue';
// @ts-ignore
import { ref, onMounted, watch } from 'vue';

interface Props {
  projectId: string | number;
}


export default /*@__PURE__*/_defineComponent({
  __name: 'BudgetConsumptionReport',
  props: {
    projectId: {}
  },
  emits: ['task-selected', 'intervenant-selected'],
  setup(__props: any, { emit: __emit }) {

const props = __props;

const { t } = useI18n();
const { notifyError } = useNotifications();

const emit = __emit;

// State
const report = ref<any | null>(null);
const loading = ref<boolean>(false);
const error = ref<string | null>(null);
const activeTab = ref<string>('intervenants');

// Formatted currency values
const formattedTotalBudget = ref('-');
const formattedTotalConsumed = ref('-');
const formattedTotalPayments = ref('-');
const formattedRemainingBalance = ref('-');

// Lifecycle hooks
onMounted(async () => {
  await loadReport();
});

// Methods
const loadReport = async () => {
  loading.value = true;
  error.value = null;
  
  try {
    const response = await reportService.getBudgetConsumptionReport(props.projectId);
    report.value = response;
  } catch (err) {
    console.error('Error loading budget consumption report:', err);
    error.value = t('reports.errorLoading');
  } finally {
    loading.value = false;
  }
};

const exportPDF = async () => {
  try {
    const blob = await reportService.exportBudgetReportPDF(props.projectId);
    const url = window.URL.createObjectURL(blob instanceof Blob ? blob : new Blob([blob]));
    const link = document.createElement('a');
    link.href = url;
    link.download = `budget-report-${props.projectId}-${Date.now()}.pdf`;
    link.click();
    window.URL.revokeObjectURL(url);
  } catch (err) {
    console.error('Error exporting PDF:', err);
    const e: any = err;
    notifyError(t('reports.exportError'));
  }
};

const exportExcel = async () => {
  try {
    const blob = await reportService.exportBudgetReportExcel(props.projectId);
    const url = window.URL.createObjectURL(blob instanceof Blob ? blob : new Blob([blob]));
    const link = document.createElement('a');
    link.href = url;
    link.download = `budget-report-${props.projectId}-${Date.now()}.xlsx`;
    link.click();
    window.URL.revokeObjectURL(url);
  } catch (err) {
    console.error('Error exporting Excel:', err);
    const e: any = err;
    notifyError(t('reports.exportError'));
  }
};

const onTaskClick = (task: any) => {
  emit('task-selected', task);
};

const onIntervenantClick = (intervenant: any) => {
  emit('intervenant-selected', intervenant);
};

const getProgressBarClass = (percentage: number) => {
  if (percentage >= 100) return 'app-progress--over';
  if (percentage >= 80) return 'app-progress--high';
  if (percentage >= 60) return 'app-progress--medium';
  return 'app-progress--low';
};

const getPaymentPercentage = (r: any) => {
  if (!r || !r.totalBudget || r.totalBudget === 0) return 0;
  const paymentAmount = r.totalPayments || 0;
  return Math.round((paymentAmount / r.totalBudget) * 100 * 100) / 100;
};

const getPaymentProgressBarClass = (percentage: number) => {
  if (percentage >= 100) return 'app-progress--payment-complete';
  if (percentage >= 80) return 'app-progress--payment-high';
  if (percentage >= 60) return 'app-progress--payment-medium';
  return 'app-progress--payment-low';
};

// Update formatted values when report changes
watch(report, async (newReport: any) => {
  if (newReport) {
    formattedTotalBudget.value = await formatCurrencyWithUserPreference(newReport.totalBudget);
    formattedTotalConsumed.value = await formatCurrencyWithUserPreference(newReport.totalConsumed);
    formattedTotalPayments.value = await formatCurrencyWithUserPreference(newReport.totalPayments || 0);
    formattedRemainingBalance.value = await formatCurrencyWithUserPreference((newReport.totalBudget - newReport.totalPayments) || 0);
  }
}, { immediate: true });

return (_ctx: any,_cache: any) => {
  return (_openBlock(), _createElementBlock("div", _hoisted_1, [
    _createElementVNode("div", _hoisted_2, [
      _createElementVNode("h2", _hoisted_3, _toDisplayString(_ctx.$t('reports.budgetConsumption.title')), 1),
      _createElementVNode("div", _hoisted_4, [
        _createVNode(_unref(Button), {
          label: _ctx.$t('common.refresh'),
          icon: "pi pi-refresh",
          onClick: loadReport,
          loading: loading.value,
          class: "app-button app-button--medium app-button--outline"
        }, null, 8, ["label", "loading"]),
        _createVNode(_unref(Button), {
          label: _ctx.$t('reports.exportPDF'),
          icon: "pi pi-file-pdf",
          onClick: exportPDF,
          disabled: !report.value,
          class: "app-button app-button--medium app-button--danger"
        }, null, 8, ["label", "disabled"]),
        _createVNode(_unref(Button), {
          label: _ctx.$t('reports.exportExcel'),
          icon: "pi pi-file-excel",
          onClick: exportExcel,
          disabled: !report.value,
          class: "app-button app-button--medium app-button--success"
        }, null, 8, ["label", "disabled"])
      ])
    ]),
    (loading.value)
      ? (_openBlock(), _createElementBlock("div", _hoisted_5, [
          _createVNode(_unref(ProgressSpinner)),
          _createElementVNode("p", null, _toDisplayString(_ctx.$t('common.loading')) + "...", 1)
        ]))
      : (error.value)
        ? (_openBlock(), _createElementBlock("div", _hoisted_6, [
            _cache[2] || (_cache[2] = _createElementVNode("i", {
              class: "pi pi-exclamation-triangle",
              "aria-hidden": "true"
            }, null, -1)),
            _createElementVNode("p", null, _toDisplayString(error.value), 1)
          ]))
        : (report.value)
          ? (_openBlock(), _createElementBlock("div", _hoisted_7, [
              _createElementVNode("h3", _hoisted_8, [
                _cache[3] || (_cache[3] = _createElementVNode("i", {
                  class: "pi pi-chart-pie",
                  "aria-hidden": "true"
                }, null, -1)),
                _createTextVNode(" " + _toDisplayString(_ctx.$t('reports.budgetConsumption.overallSummary')), 1)
              ]),
              _createElementVNode("div", _hoisted_9, [
                _createElementVNode("div", _hoisted_10, [
                  _createElementVNode("span", _hoisted_11, _toDisplayString(_ctx.$t('reports.totalBudget')), 1),
                  _createElementVNode("span", _hoisted_12, _toDisplayString(formattedTotalBudget.value), 1)
                ]),
                _createElementVNode("div", _hoisted_13, [
                  _createElementVNode("span", _hoisted_14, _toDisplayString(_ctx.$t('reports.totalConsumed')), 1),
                  _createElementVNode("span", _hoisted_15, _toDisplayString(formattedTotalConsumed.value), 1)
                ]),
                _createElementVNode("div", _hoisted_16, [
                  _createElementVNode("span", _hoisted_17, _toDisplayString(_ctx.$t('reports.totalPaid')), 1),
                  _createElementVNode("span", _hoisted_18, _toDisplayString(formattedTotalPayments.value), 1)
                ]),
                _createElementVNode("div", _hoisted_19, [
                  _createElementVNode("span", _hoisted_20, _toDisplayString(_ctx.$t('reports.budget.remaining')), 1),
                  _createElementVNode("span", _hoisted_21, _toDisplayString(formattedRemainingBalance.value), 1)
                ])
              ]),
              _createElementVNode("div", _hoisted_22, [
                _createElementVNode("div", _hoisted_23, [
                  _createElementVNode("span", _hoisted_24, _toDisplayString(_ctx.$t('reports.budgetProgress')), 1),
                  _createElementVNode("span", _hoisted_25, _toDisplayString(_unref(formatPercentage)(report.value.consumptionPercentage)), 1)
                ]),
                _createVNode(_unref(ProgressBar), {
                  value: report.value.consumptionPercentage,
                  class: _normalizeClass(getProgressBarClass(report.value.consumptionPercentage))
                }, null, 8, ["value", "class"])
              ]),
              _createElementVNode("div", _hoisted_26, [
                _createElementVNode("div", _hoisted_27, [
                  _createElementVNode("span", _hoisted_28, _toDisplayString(_ctx.$t('reports.paymentProgress')), 1),
                  _createElementVNode("span", _hoisted_29, _toDisplayString(_unref(formatPercentage)(getPaymentPercentage(report.value))), 1)
                ]),
                _createVNode(_unref(ProgressBar), {
                  value: getPaymentPercentage(report.value),
                  class: _normalizeClass(getPaymentProgressBarClass(getPaymentPercentage(report.value)))
                }, null, 8, ["value", "class"])
              ]),
              _createElementVNode("div", _hoisted_30, [
                _createElementVNode("div", _hoisted_31, [
                  _createElementVNode("button", {
                    class: _normalizeClass(["app-tab", { 'app-tab--active': activeTab.value === 'intervenants' }]),
                    onClick: _cache[0] || (_cache[0] = ($event: any) => (activeTab.value = 'intervenants'))
                  }, _toDisplayString(_ctx.$t('reports.intervenantsBudget')), 3),
                  _createElementVNode("button", {
                    class: _normalizeClass(["app-tab", { 'app-tab--active': activeTab.value === 'tasks' }]),
                    onClick: _cache[1] || (_cache[1] = ($event: any) => (activeTab.value = 'tasks'))
                  }, _toDisplayString(_ctx.$t('reports.tasksBudget')), 3)
                ]),
                (activeTab.value === 'intervenants')
                  ? (_openBlock(), _createBlock(IntervenantBudgetReport, {
                      key: 0,
                      intervenants: report.value.intervenantBudgetReports,
                      onIntervenantClick: onIntervenantClick
                    }, null, 8, ["intervenants"]))
                  : _createCommentVNode("", true),
                (activeTab.value === 'tasks')
                  ? (_openBlock(), _createBlock(TaskBudgetReport, {
                      key: 1,
                      tasks: report.value.taskBudgetReports,
                      onTaskClick: onTaskClick
                    }, null, 8, ["tasks"]))
                  : _createCommentVNode("", true)
              ])
            ]))
          : _createCommentVNode("", true)
  ]))
}
}

})